/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { type Insight as BaseInsight, registerStandardChartInsight } from '../insightManager';
import { toArray } from '../../util/data';
import {
  findSeriesDataItemInfo,
  getSingletonInsightDvMarkerSeries,
  mergeSeriesDataItem,
  getDataItemValue
} from '../../helper/standardChart';
import {
  expandRange,
  handleMultipleSeries,
  handleAnimation,
  isMultipleSeriesWithoutDvMarker
} from '../helper';

type InsightData = {
  /** x 轴指标对应数据集字段 */
  x: string;
  /** y 轴指标对应数据集字段 */
  y: string;
  /** 要标记的 x 轴指标起始数据值 */
  xStart: string;
  /** 要标记的 x 轴指标终止数据值 */
  xEnd: string;
  /** 要标记的 y 轴指标起始数据值 */
  yStart?: string;
  /** 要标记的 y 轴指标终止数据值 */
  yEnd?: string;
  /** 柱状图下区间高亮 */
  rangeHighlight?: boolean;
  /** 文本描述 */
  summary?: string;
  /** 文本摘要 @see https://datav.iwencai.com/components/paradigm-chart/docs/apis/series/dvMarker/#seriesdvitemstyle */
  textSummary?: unknown;
  /** 线的配置 @see https://echarts.apache.org/zh/option.html#series-bar.markLine.data */
  line?: unknown;
  /** 弱化系列 */
  blurSeries?: unknown;
};

const type = 'Trend';
const INSIGHT_FLAG = `insight_${type}`;

interface Insight extends BaseInsight<InsightData[]> {
  readonly type: typeof type;
}

registerStandardChartInsight<Insight>({
  type,
  filter: option => {
    return (
      toArray(option.xAxis).length > 0 &&
      toArray(option.yAxis).length > 0 &&
      !toArray(option.series).some(item => !['line', 'bar', 'scatter'].includes(item.type))
    );
  },
  clear: {
    onOptionUpdate(option) {
      option.series = toArray(option.series).map(series => {
        series.markLine = series.markLine || {};
        series.markLine.data = (series.markLine.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );

        return series;
      });
      if (option?.legend?.data) {
        option.legend.data = option.legend.data?.map(legendItem => {
          delete (legendItem as { __blur?: number }).__blur;
          return legendItem;
        });
      }
    }
  },
  render: {
    onOptionUpdate(context, option, legendIns) {
      const insight = context.insightOption;
      const { linkLegend = false } = insight;
      const { animationDuration = 0 } = insight;
      const isMultipleSeries = isMultipleSeriesWithoutDvMarker(option);

      function renderLine(data: InsightData) {
        const {
          x,
          y,
          xStart,
          xEnd,
          yStart,
          yEnd,
          rangeHighlight = false,
          summary,
          line: lineOpt = {},
          textSummary: textSummaryOpt = {},
          blurSeries = {}
        } = data;
        if (xStart === xEnd) {
          return;
        }
        const blurSeriesOpt = JSON.parse(JSON.stringify(blurSeries));
        const startDataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          xValue: xStart,
          yValue: yStart
        });
        if (!startDataItemMetaInfo) {
          return;
        }
        const endDataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          xValue: xEnd,
          yValue: yEnd
        });
        if (
          !endDataItemMetaInfo ||
          startDataItemMetaInfo.seriesIndex !== endDataItemMetaInfo.seriesIndex
        ) {
          return;
        }
        const {
          xAxisIndex,
          yAxisIndex,
          seriesIndex,
          dataItemIndex: startDataItemIndex
        } = startDataItemMetaInfo;
        const { dataItemIndex: endDataItemIndex } = endDataItemMetaInfo;
        const series = context.util.data.toArray(option.series)[seriesIndex];

        const tempStartPoint = getDataItemValue(series.data[startDataItemIndex]);
        const startPoint = [xStart, (tempStartPoint as [number, number])[1] || tempStartPoint];
        const tempEndPoint = getDataItemValue(series.data[endDataItemIndex]);
        const endPoint = [xEnd, (tempEndPoint as [number, number])[1] || tempEndPoint];
        const midPointDataItemIndex = Math.floor((startDataItemIndex + endDataItemIndex) / 2);
        let midX;
        let midY;
        // 散点图的x轴是数值轴，与类目轴存在差异
        if (series.type !== 'scatter') {
          midX = getDataItemValue(
            context.util.data.toArray(option.xAxis)[xAxisIndex]!.data[midPointDataItemIndex]
          );
          // 斜率
          const m = +(
            ((startPoint as number[])[1] - (endPoint as number[])[1]) /
            (+startDataItemIndex - +endDataItemIndex)
          );
          // 截距
          const b = (endPoint as number[])[1] - m * endDataItemIndex;

          midY = m * +midPointDataItemIndex + b;
        } else {
          midX = (+endPoint[0] + +startPoint[0]) / 2;
          const m = +(
            ((startPoint as number[])[1] - (endPoint as number[])[1]) /
            ((startPoint as number[])[0] - (endPoint as number[])[0])
          );
          const b = (endPoint as number[])[1] - m * (endPoint as number[])[0];
          midY = m * +midX + b;
        }
        /** 将第一个数值转为字符串，针对curvedPoint获取类目轴数据 */
        const midPoint = [String(midX), midY];

        series.markLine = series.markLine || {};
        series.markLine.data = series.markLine.data || [];

        const line = [
          {
            ...(lineOpt as any),
            symbol: 'none',
            coord: startPoint
          },
          {
            ...(lineOpt as any),
            coord: endPoint
          }
        ];
        (line as unknown as { __insightFlag: string }).__insightFlag = INSIGHT_FLAG;

        series.markLine.data.push(line);

        /** 曲线文本 */
        if (summary) {
          const markerSeries = getSingletonInsightDvMarkerSeries(option, {
            xAxisIndex,
            yAxisIndex
          });
          const pointSummary = {
            value: midPoint,
            __seriesIndex: seriesIndex,
            itemStyle: context.util.data.deepMerge(
              {
                textStyle: {
                  style: {
                    text: summary
                  }
                }
              },
              textSummaryOpt
            )
          };

          markerSeries.data.push(pointSummary as unknown as number);
          handleAnimation(series.markLine, animationDuration, markerSeries);
        } else {
          handleAnimation(series.markLine, animationDuration);
        }

        // * 弱化主图
        option.series = context.util.data.toArray(option.series);
        if (!option.series[seriesIndex].areaStyle) {
          delete blurSeriesOpt.areaStyle;
        }

        if (isMultipleSeries) {
          // TODO 多系列下柱状图的弱化效果不同
          handleMultipleSeries(option, seriesIndex, legendIns!, blurSeriesOpt, linkLegend);
          return;
        }

        if (series.type === 'bar') {
          let highlightArray = [startDataItemIndex, endDataItemIndex];
          if (rangeHighlight) {
            highlightArray = expandRange(highlightArray);
          }
          series.data = series.data.map((dataItem, index) => {
            if (
              highlightArray.includes(index) ||
              (dataItem as unknown as { __noBlur?: boolean })?.__noBlur
            ) {
              return mergeSeriesDataItem(dataItem, { __noBlur: true });
            }

            return mergeSeriesDataItem(dataItem, blurSeriesOpt);
          });
        }
        if (series.type === 'line') {
          option.series = context.util.data.toArray(option.series);
          option.series[seriesIndex] = context.util.data.deepMerge(series, blurSeriesOpt);
        }
      }

      insight.data.forEach(item => renderLine(item));
    }
  }
});
